/**
 * Responsive variant lookup for bundled images.
 *
 * Every image in `src/assets/*.webp` has pre-generated AVIF + WebP variants in
 * `src/assets/responsive/<stem>-<width>.{avif,webp}`. This module maps a
 * hashed build URL (e.g. `/assets/hero-heart-v2.a1b2c3.webp`) back to the
 * srcset strings for those variants so <picture> can serve the smallest
 * acceptable file and modern formats where supported.
 */

const avifModules = import.meta.glob("../assets/responsive/*.avif", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

const webpModules = import.meta.glob("../assets/responsive/*.webp", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

type Variant = { width: number; url: string };

const build = (mods: Record<string, string>) => {
  const map = new Map<string, Variant[]>();
  for (const [path, url] of Object.entries(mods)) {
    const file = path.split("/").pop() ?? "";
    const match = file.match(/^(.*)-(\d+)\.(?:avif|webp)$/);
    if (!match) continue;
    const [, stem, width] = match;
    const list = map.get(stem) ?? [];
    list.push({ width: Number(width), url });
    map.set(stem, list);
  }
  for (const list of map.values()) list.sort((a, b) => a.width - b.width);
  return map;
};

const avifMap = build(avifModules);
const webpMap = build(webpModules);

const toSrcSet = (variants?: Variant[]) =>
  variants?.length ? variants.map((v) => `${v.url} ${v.width}w`).join(", ") : undefined;

/** Extracts the original asset stem from a (possibly hashed) bundled URL. */
export const assetStem = (src: string): string => {
  const file = src.split("/").pop() ?? "";
  return file.replace(/\.[a-z0-9]+$/i, "").replace(/-[A-Za-z0-9_]{8}$/, "");
};

export const responsiveSources = (src: string) => {
  const stem = assetStem(src);
  return {
    avif: toSrcSet(avifMap.get(stem)),
    webp: toSrcSet(webpMap.get(stem)),
  };
};
